/**
 * <ts-predictive-search> — Athos-powered predictive search for the header overlay.
 *
 * Empty/focus state: trending queries ("Top searches") + products for the top
 * trending query ("Popular items"). While typing: term suggestions (/suggest),
 * facet groups + product tiles + a result-count CTA (/autocomplete). Athos
 * autocomplete needs a completed term, so suggest() resolves the partial first.
 *
 * The surrounding <form> submits natively to the search results page, so search
 * still works if the suggestion/autocomplete calls fail (progressive enhancement).
 *
 * @module ts-athos-predictive-search
 */

import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { searchSpringClient } from '@ts/searchspring-client';
import { escapeHtml, escapeAttr } from '@ts/utility-html-escape';
// Registers <ss-product-card> — the overlay reuses the same card UI as the collection grid.
import '@ts/searchspring-product-card';
import { trackResults, trackProductClick } from '@ts/athos-beacon';

const DEBOUNCE_MS = 250;
const MAX_TERMS = 6;
const MAX_FACET_GROUPS = 3;
const MAX_FACET_VALUES = 5;

/**
 * @typedef {Object} Refs
 * @property {HTMLInputElement} searchInput - The search text input.
 * @property {HTMLElement} panels - Container the component renders results into.
 * @property {HTMLFormElement} form - The search form (native GET submit).
 * @property {HTMLElement} [guidance] - Inline guidance live region.
 * @property {HTMLElement} [status] - Live region announcing the result count.
 * @extends {Component<Refs>}
 */
class AthosPredictiveSearch extends Component {
  requiredRefs = ['searchInput', 'panels', 'form'];

  #lifecycle = new AbortController();

  /** @type {AbortController | null} In-flight request controller. */
  #request = null;

  /**
   * Cached trending/empty state — fetched once per page, so reopening the
   * overlay neither re-queries the API nor re-reports beacon events.
   * @type {{ queries: string[], products: any[], responseId: string } | null}
   */
  #emptyState = null;

  /** @type {() => void} */
  #debouncedSearch = () => {};

  /**
   * The owning <ts-search-modal>. This component is purpose-built for the
   * search overlay and must be rendered inside one (see ts-search-overlay.liquid)
   * — a generic dialog-component wouldn't provide the overlay's positioning or
   * scroll-lock behavior.
   */
  get dialog() {
    return this.closest('ts-search-modal');
  }

  /** Config sourced from data-* attributes (set in the Liquid snippet). */
  get #config() {
    const d = this.dataset;
    return {
      searchUrl: d.searchUrl || '/search',
      productCount: Number(d.productCount) || 8,
      trendingLimit: Number(d.trendingLimit) || 6,
      labels: {
        topSearches: d.labelTopSearches || '',
        popular: d.labelPopular || '',
        recommended: d.labelRecommended || '',
      },
      ctaTemplate: d.ctaTemplate || '[count] results',
      noResultsTemplate: d.noResultsTemplate || 'No results found for "[terms]".',
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.#debouncedSearch = debounce(() => this.#search(), DEBOUNCE_MS);

    // Fresh controller per connect: disconnectedCallback aborts the old one, so a
    // reconnect (theme editor section re-render) must re-arm listeners with a live
    // signal rather than the already-aborted one from the initial connect.
    this.#lifecycle = new AbortController();

    const { signal } = this.#lifecycle;
    const dialog = this.dialog;
    if (dialog) {
      dialog.addEventListener('dialog:open', this.#onDialogOpen, { signal });
    }

    // The dialog element fills the area below the header and doubles as the
    // scrim, so dialog.js's click-outside check never fires for clicks on the
    // dimmed page beneath the panel — close on those directly.
    const dialogEl = this.closest('dialog');
    if (dialogEl) {
      dialogEl.addEventListener('click', this.#onScrimClick, { signal });
    }

    // Beacon clickThrough for product links in the overlay grid.
    this.addEventListener('click', (event) => trackProductClick('autocomplete', event), { signal });
  }

  /** @param {MouseEvent} event */
  #onScrimClick = (event) => {
    if (event.target !== event.currentTarget) return;
    const dialog = /** @type {any} */ (this.dialog);
    dialog?.closeDialog();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#lifecycle.abort();
    this.#request?.abort();
  }

  #onDialogOpen = () => {
    // The overlay's top offset is set by <ts-search-modal> in showDialog() —
    // nothing to measure here.
    // preventScroll: iOS pans the layout viewport to "reveal" a focused input —
    // pointless inside a fixed dialog, and the pan shoves the overlay offscreen.
    requestAnimationFrame(() => this.refs.searchInput?.focus({ preventScroll: true }));

    if (this.refs.searchInput.value.trim()) return;

    // On the search results page the overlay opens seeded with the active query
    // (config.searchQuery) and shows its predictive results — so editing the
    // results-page search bar behaves exactly like the header overlay.
    const seed = window.__searchspringConfig?.searchQuery?.trim();
    if (seed) {
      this.refs.searchInput.value = seed;
      this.#search();
      return;
    }

    this.#renderEmptyState();
  };

  /** Bound via on:input. */
  onInput() {
    const value = this.refs.searchInput.value;
    this.#clearGuidance();

    if (!value.trim()) {
      this.#renderEmptyState();
      return;
    }
    this.#debouncedSearch();
  }

  /**
   * Bound via on:submit — block empty/whitespace submissions (AC9).
   * @param {Event} event
   */
  onSubmit(event) {
    if (!this.refs.searchInput.value.trim()) {
      event.preventDefault();
      this.#showGuidance();
      this.refs.searchInput.focus();
    }
  }

  /** Abort any in-flight request and start a fresh one. */
  #newRequest() {
    this.#request?.abort();
    this.#request = new AbortController();
    return this.#request;
  }

  async #renderEmptyState() {
    if (this.#emptyState) {
      // Cancel any in-flight typed search so it can't overwrite this render.
      this.#request?.abort();
      this.#renderEmptyStateContent(this.#emptyState);
      return;
    }

    const { trendingLimit, productCount } = this.#config;
    const controller = this.#newRequest();

    try {
      const trending = await searchSpringClient.trending({ limit: trendingLimit, controller });
      const queries = (trending?.trending?.queries || []).map((/** @type {any} */ q) => q.searchQuery).filter(Boolean);

      /** @type {any[]} */
      let products = [];
      /** @type {string} */
      let responseId = '';
      if (queries[0]) {
        const results = await searchSpringClient.autocomplete({ q: queries[0], productCount, controller });
        products = results?.results || [];
        responseId = results?.responseId || '';
      }
      this.#emptyState = { queries, products, responseId };
      this.#renderEmptyStateContent(this.#emptyState);
    } catch (error) {
      if (this.#isAbort(error)) return;
      // Empty state is non-critical; leave the panel blank rather than error.
      this.refs.panels.innerHTML = '';
      this.#setStatus('');
    }
  }

  /**
   * Paint the trending/empty state and (re-)arm beacon attribution — the
   * beacon dedupes render/impression per responseId, so re-renders of the
   * cached state don't re-report.
   * @param {{ queries: string[], products: any[], responseId: string }} state
   */
  #renderEmptyStateContent({ queries, products, responseId }) {
    this.refs.panels.innerHTML = this.#emptyMarkup(queries, products.length > 0);
    this.#populateGrid(products);
    trackResults('autocomplete', responseId, products);
    // Trending/empty state is not a result count — keep the live region quiet.
    this.#setStatus('');
  }

  async #search() {
    const query = this.refs.searchInput.value.trim();
    if (!query) return this.#renderEmptyState();

    const { productCount } = this.#config;
    const controller = this.#newRequest();

    try {
      // Suggestions are non-critical: a non-abort failure just means no term
      // list (resolve to null); an abort rethrows and is handled below.
      const suggest = await searchSpringClient.suggest({ q: query, controller }).catch((error) => {
        if (this.#isAbort(error)) throw error;
        return null;
      });
      const completed = this.#completedTerm(query, suggest);

      const results = await searchSpringClient.autocomplete({ q: completed, productCount, controller });
      const products = results?.results || [];
      const facets = results?.facets || [];
      const total = results?.pagination?.totalResults || 0;
      const terms = this.#terms(query, suggest);
      this.refs.panels.innerHTML = this.#resultsMarkup(query, completed, terms, facets, products.length > 0, total);
      this.#populateGrid(products);
      trackResults('autocomplete', results?.responseId || '', products);
      this.#setStatus(
        total > 0
          ? this.#config.ctaTemplate.replace('[count]', String(total)).replace('[query]', query)
          : this.#config.noResultsTemplate.replace('[terms]', query)
      );
    } catch (error) {
      if (this.#isAbort(error)) return;
      // Autocomplete failed (timeout/500/offline). The <form> still submits to the
      // results page, so search is not broken — just clear the panel rather than
      // show misleading "no results" copy for what is actually an API error.
      this.refs.panels.innerHTML = '';
      this.#setStatus('');
    }
  }

  /** @param {unknown} error */
  #isAbort(error) {
    return error instanceof Error && error.name === 'AbortError';
  }

  /**
   * Best completed term for the autocomplete call (falls back to the raw query).
   * @param {string} query
   * @param {any} suggest
   * @returns {string}
   */
  #completedTerm(query, suggest) {
    // Athos exposes a spell correction as the top-level `corrected-query`.
    // suggested.type stays "exact" even when the spelling was changed, so it
    // can't gate this — prefer the correction whenever it differs from the input.
    const corrected = suggest?.['corrected-query'];
    if (corrected && corrected.toLowerCase() !== query.toLowerCase()) return corrected;

    // No spelling change: resolve a partial into a full term for autocomplete.
    const suggested = suggest?.suggested;
    if (!suggested) return query;
    if (suggested.type === 'exact') return query;
    return suggested.completed?.[0]?.query || suggested.text || query;
  }

  /**
   * Deduped list of suggested terms for the left column.
   * @param {string} query
   * @param {any} suggest
   * @returns {string[]}
   */
  #terms(query, suggest) {
    const suggested = suggest?.suggested;
    const raw = [
      suggested?.text,
      ...(suggested?.completed || []).map((/** @type {any} */ c) => c.query),
      ...(suggest?.alternatives || []).map((/** @type {any} */ a) => a.text),
    ].filter(Boolean);

    /** @type {string[]} */
    const terms = [];
    const seen = new Set();
    for (const term of raw) {
      const key = term.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      terms.push(term);
      if (terms.length >= MAX_TERMS) break;
    }
    return terms;
  }

  /**
   * Build a results-page URL for a term (+ optional facet deep-link).
   * @param {string} term
   * @param {{ field: string, value: string }} [facet]
   * @returns {string}
   */
  #searchUrl(term, facet) {
    const { searchUrl } = this.#config;
    let url = `${searchUrl}?q=${encodeURIComponent(term)}`;
    if (facet) url += `#/filter.${facet.field}=${encodeURIComponent(facet.value)}`;
    return url;
  }

  #showGuidance() {
    if (this.refs.guidance) this.refs.guidance.hidden = false;
  }

  #clearGuidance() {
    if (this.refs.guidance) this.refs.guidance.hidden = true;
  }

  /**
   * Announce the result count in the dedicated live region (the panel itself is
   * silent, so screen readers hear "See N results" rather than the whole grid).
   * @param {string} text
   */
  #setStatus(text) {
    if (this.refs.status) this.refs.status.textContent = text;
  }

  //#region - Markup

  /**
   * @param {string} heading - Column heading; omit ('') to render no heading (typing state).
   * @param {string[]} terms
   * @param {boolean} [activeFirst] - Mark the first term as the active/current query.
   * @returns {string}
   */
  #termList(heading, terms, activeFirst = false) {
    if (!terms.length) return '';
    const items = terms
      .map((term, i) => {
        const active = activeFirst && i === 0 ? ' ts-search-overlay__term--active' : '';
        return `<li><a class="ts-search-overlay__term${active}" href="${escapeAttr(this.#searchUrl(term))}">${escapeHtml(term)}</a></li>`;
      })
      .join('');
    return `
      <div class="ts-search-overlay__col ts-search-overlay__col--terms">
        ${heading ? `<h2 class="ts-search-overlay__col-title">${escapeHtml(heading)}</h2>` : ''}
        <ul class="ts-search-overlay__term-list">${items}</ul>
      </div>`;
  }

  /**
   * Product column skeleton — the grid is filled with <ss-product-card> elements
   * by #populateGrid after the markup is inserted (same card UI as the PLP).
   * @param {string} heading
   * @returns {string}
   */
  #productColumn(heading) {
    return `
      <div class="ts-search-overlay__col ts-search-overlay__col--products">
        <h2 class="ts-search-overlay__col-title">${escapeHtml(heading)}</h2>
        <div class="ts-search-overlay__grid" data-ss-overlay-grid></div>
      </div>`;
  }

  /**
   * Instantiate <ss-product-card> elements from Athos results into the panel grid.
   * @param {any[]} products
   */
  #populateGrid(products) {
    const grid = this.refs.panels.querySelector('[data-ss-overlay-grid]');
    if (!grid) return;
    products.forEach((product, index) => {
      const card = /** @type {any} */ (document.createElement('ss-product-card'));
      grid.appendChild(card);
      card.setResult(product, index);
      // No resolver hydration in the overlay — on non-USD markets the card
      // defers its price paint, so opt into the approximate feed-converted one.
      card.setPrice(null);
    });
  }

  /**
   * @param {string} term - Term the facets were computed from; used for the link URLs.
   * @param {any[]} facets
   * @returns {string}
   */
  #facetGroups(term, facets) {
    const groups = facets
      .filter((/** @type {any} */ f) => (f.values || []).length)
      .slice(0, MAX_FACET_GROUPS)
      .map((/** @type {any} */ facet) => {
        const values = facet.values
          .slice(0, MAX_FACET_VALUES)
          .map(
            (/** @type {any} */ v) =>
              `<li><a href="${escapeAttr(this.#searchUrl(term, { field: facet.field, value: v.value }))}">${escapeHtml(v.label || v.value)}</a></li>`
          )
          .join('');
        return `
          <div class="ts-search-overlay__facet">
            <h3 class="ts-search-overlay__facet-title">${escapeHtml(facet.label || facet.field)}</h3>
            <ul class="ts-search-overlay__facet-values">${values}</ul>
          </div>`;
      })
      .join('');

    if (!groups) return '';
    return `<div class="ts-search-overlay__col ts-search-overlay__col--facets">${groups}</div>`;
  }

  /**
   * @param {string} query - Raw user input, shown in the label.
   * @param {string} term - Term the results were computed from; used for the link URL.
   * @param {number} total
   * @returns {string}
   */
  #cta(query, term, total) {
    if (!total) return '';
    const label = this.#config.ctaTemplate.replace('[count]', String(total)).replace('[query]', query);
    return `<a class="ts-search-overlay__cta" href="${escapeAttr(this.#searchUrl(term))}">${escapeHtml(label)}</a>`;
  }

  /**
   * @param {string[]} queries
   * @param {boolean} hasProducts
   * @returns {string}
   */
  #emptyMarkup(queries, hasProducts) {
    const { labels } = this.#config;
    return `
      <div class="ts-search-overlay__layout ts-search-overlay__layout--empty">
        ${this.#termList(labels.topSearches, queries)}
        ${hasProducts ? this.#productColumn(labels.popular) : ''}
      </div>`;
  }

  /**
   * @param {string} query - Raw user input, used for display labels.
   * @param {string} term - Spell-corrected term the results/facets came from; used for link URLs.
   * @param {string[]} terms
   * @param {any[]} facets
   * @param {boolean} hasProducts
   * @param {number} total
   * @returns {string}
   */
  #resultsMarkup(query, term, terms, facets, hasProducts, total) {
    const { labels } = this.#config;
    if (!hasProducts && !terms.length) {
      return this.#noResultsMarkup(query);
    }
    return `
      <div class="ts-search-overlay__layout ts-search-overlay__layout--results">
        <div class="ts-search-overlay__menu">
          ${this.#termList('', terms, true)}
          ${this.#facetGroups(term, facets)}
        </div>
        ${hasProducts ? this.#productColumn(labels.recommended) : ''}
      </div>
      ${this.#cta(query, term, total)}`;
  }

  /**
   * @param {string} query
   * @returns {string}
   */
  #noResultsMarkup(query) {
    const message = this.#config.noResultsTemplate.replace('[terms]', query);
    return `<div class="ts-search-overlay__message">${escapeHtml(message)}</div>`;
  }

  //#endregion
}

if (!customElements.get('ts-predictive-search')) {
  customElements.define('ts-predictive-search', AthosPredictiveSearch);
}

export { AthosPredictiveSearch };
